/**
 * Three mutually exclusive diversification groups (F11b, plan §3.3 · §4.4).
 *
 * The groups share ONE `used` set, so the evaluation order below is a product
 * decision, not an implementation detail: a candidate qualifying for two groups
 * lands in whichever is evaluated FIRST and is then unavailable to the rest.
 * The order is `uncorrelated → inverse → similar`, matching the reference, and
 * §4.4 requires it to be asserted by constructing a dual-qualifying candidate.
 *
 * Empty groups are RETAINED rather than dropped: the stock screen keeps its
 * panel slots so layout and keyboard order stay stable when a rule finds nobody
 * (plan §6.5a), and an absent group would be indistinguishable from an absent
 * rule.
 */
import type {
  DiversificationGroup,
  DiversificationKind,
  SectorId,
  Ticker,
  WindowKey,
} from '@meridian/contracts';

import { COLLATOR } from '../constants/collation';
import {
  DIVERSIFICATION_GROUP_SIZE,
  INVERSE_THRESHOLD,
  SIMILAR_THRESHOLD,
} from '../constants/diversification';

import { rankByCorrelation, type FactorSecurity, type RankedCorrelation } from './correlation';

export interface DiversificationCandidate extends FactorSecurity {
  readonly sc: SectorId;
  readonly ret: Readonly<Record<WindowKey, number | null>>;
}

type Scored = RankedCorrelation<DiversificationCandidate>;

/** Ordering within a group; the ρ threshold filter is applied before this. */
interface GroupRule {
  readonly kind: DiversificationKind;
  readonly select: (scored: readonly Scored[]) => readonly Scored[];
}

function byTickerAsc(a: Scored, b: Scored): number {
  return COLLATOR.compare(a.ticker, b.ticker);
}

function byAbsRhoAscending(a: Scored, b: Scored): number {
  return Math.abs(a.rho) - Math.abs(b.rho) || byTickerAsc(a, b);
}

/**
 * Return-proximity ordering for SIMILAR. A null return sorts last — "closest
 * return" is meaningless for a row that reports none, and treating null as 0
 * would make unreported rows look closest to any flat subject.
 *
 * When the subject itself has no return, every comparison is a pure ticker
 * tie-break so the group order is still a pure function of the candidate set.
 */
function byReturnProximity(subjectRet: number | null, window: WindowKey) {
  return (a: Scored, b: Scored): number => {
    if (subjectRet === null) return byTickerAsc(a, b);
    const x = a.candidate.ret[window];
    const y = b.candidate.ret[window];
    if (x === null) return y === null ? byTickerAsc(a, b) : 1;
    if (y === null) return -1;
    return Math.abs(x - subjectRet) - Math.abs(y - subjectRet) || byTickerAsc(a, b);
  };
}

function rulesFor(subject: DiversificationCandidate, window: WindowKey): readonly GroupRule[] {
  const subjectRet = subject.ret[window];
  return [
    { kind: 'uncorrelated', select: (s) => [...s].sort(byAbsRhoAscending) },
    {
      kind: 'inverse',
      select: (s) =>
        s
          .filter((x) => x.rho < INVERSE_THRESHOLD)
          .sort((a, b) => a.rho - b.rho || byTickerAsc(a, b)),
    },
    {
      kind: 'similar',
      select: (s) =>
        s
          .filter((x) => x.candidate.sc !== subject.sc && x.rho < SIMILAR_THRESHOLD)
          .sort(byReturnProximity(subjectRet, window)),
    },
  ];
}

/**
 * Build groups from an already-ranked pairwise scan so extremes and groups can
 * share one `rankByCorrelation` pass (F11b + F15 on the stock screen).
 */
export function diversificationGroupsFromRanked(
  subject: DiversificationCandidate,
  scored: readonly Scored[],
  window: WindowKey,
): readonly DiversificationGroup[] {
  const used = new Set<Ticker>();
  return rulesFor(subject, window).map((rule) => {
    const members = rule
      .select(scored)
      .filter((s) => !used.has(s.ticker))
      .slice(0, DIVERSIFICATION_GROUP_SIZE);
    members.forEach((m) => used.add(m.ticker));
    return { kind: rule.kind, members: members.map((m) => ({ ticker: m.ticker, rho: m.rho })) };
  });
}

export function diversificationGroups(
  subject: DiversificationCandidate,
  candidates: readonly DiversificationCandidate[],
  window: WindowKey,
): readonly DiversificationGroup[] {
  return diversificationGroupsFromRanked(subject, rankByCorrelation(subject, candidates), window);
}
